import { useEffect, useState } from 'react';

import { useFormikContext } from 'formik';

import Input from 'components/shared/Input/Input';
import Switch from 'components/shared/Switch/Switch';
import { showAlert } from 'components/shared/Alert/Alert';

const MAX_FEE_PERCENTAGE = 6;

const TechnologyFeeSettings = ({ setShowTechnologyModal }) => {
  const { setFieldValue, values } = useFormikContext();
  const [feeInput, setFeeInput] = useState('');

  useEffect(() => {
    setFeeInput(((values?.surchargeAmount ?? 0) / 100).toString());
  }, [values?.surchargeAmount]);

  const handleToggle = (checked) => {
    if (checked) {
      setShowTechnologyModal(true);
    } else {
      setFieldValue('surchargeEnabled', false);
    }
  };

  const handleChange = (event) => {
    const next = event.target.value;
    if (/^\d*\.?\d{0,2}$/.test(next)) setFeeInput(next);
  };

  const handleBlur = () => {
    const numValue = parseFloat(feeInput);

    if (Number.isNaN(numValue)) {
      setFeeInput('0');
      setFieldValue('surchargeAmount', 0);
      return;
    }

    if (numValue > MAX_FEE_PERCENTAGE) {
      showAlert({
        title: 'Invalid fee percentage',
        message: `Fee percentage cannot exceed ${MAX_FEE_PERCENTAGE}%. Value will be set to ${MAX_FEE_PERCENTAGE}%.`,
        color: 'warning'
      });
    } else if (numValue < 0) {
      showAlert({
        title: 'Invalid fee percentage',
        message: 'Fee percentage cannot be negative. Value will be set to 0%.',
        color: 'warning'
      });
    }

    const limitedValue = Math.min(Math.max(numValue, 0), MAX_FEE_PERCENTAGE);
    // Round to two decimals so surchargeAmount stays a clean integer, avoiding
    // float artifacts (e.g. 3.3 * 100 -> 330.00000000000006).
    const surchargeAmount = Math.round(limitedValue * 100);
    setFieldValue('surchargeAmount', surchargeAmount);
    setFeeInput((surchargeAmount / 100).toString());
  };

  const parsed = parseFloat(feeInput);
  const error =
    parsed > MAX_FEE_PERCENTAGE
      ? `Fee cannot exceed ${MAX_FEE_PERCENTAGE}%`
      : parsed < 0
        ? 'Fee cannot be negative'
        : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-base font-500 leading-6 text-neutral-900">Technology fee</p>
        <p className="text-sm leading-[22px] text-neutral-700">
          Apply a practice-wide technology fee to eligible payments using the percentage set below.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={values?.surchargeEnabled}
          onChange={handleToggle}
          id="technology-fee-switch"
        />
        <label htmlFor="technology-fee-switch" className="text-sm font-400 text-neutral-900">
          Technology fee
        </label>
      </div>
      {values?.surchargeEnabled && (
        <div className="flex items-center gap-4">
          <label htmlFor="technology-fee-percentage" className="text-sm text-neutral-700">
            Fee percentage
          </label>
          <Input
            id="technology-fee-percentage"
            value={feeInput}
            onChange={handleChange}
            onBlur={handleBlur}
            type="text"
            inputMode="decimal"
            rightText="%"
            className="w-[120px]"
            error={error}
          />
        </div>
      )}
    </div>
  );
};

export default TechnologyFeeSettings;
